// WHICH RENDERER THE APP'S `<Canvas>` BUILDS.
//
// THREE IS THE ONLY RENDERER, AND THIS IS THE DEFAULT: `WebGPURenderer`, on
// WebGPU where the browser has it and on three's own WebGL2 backend where it
// does not. There is no Spark, and there is no second canvas for the splats to
// live on because a node material compiles here.
//
// `WebGPURenderer` picks the backend ITSELF — it constructs a `WebGPUBackend`
// with a `getFallback` that returns a `WebGLBackend` — so "WebGPU by default,
// WebGL when it is missing" needs no detection of ours and cannot disagree with
// what three actually built. Read the backend off the renderer, never off the
// flag: a `?renderer3d=webgpu` run on a machine without WebGPU IS the fallback.
//
// ONE RENDERER, TWO BACKENDS, and the flag only chooses between the backends:
//
//   (default)        WebGPURenderer, WebGPU with three's WebGL2 fallback.
//   webgl-fallback   WebGPURenderer with `forceWebGL` — the fallback FORCED on a
//                    machine that has WebGPU. Without it that path is unreachable
//                    on any developer box and would ship unexercised, which is
//                    exactly what happened to it before.
//
// THERE WAS A THIRD, `webgl`, WHICH BUILT R3F'S CLASSIC `WebGLRenderer`, and it is
// gone. It compiled no node material, so on it the material graphs, the
// twelve-influence skinning, the environment and the splats all took a
// pre-migration path — which meant a second, complete material compiler had to
// exist for it, plus a per-component gate at every place the two differed. It was
// kept to A/B a regression against the renderer that shipped before; the price was
// a whole alternative implementation that nothing supported, and the thing it
// actually produced was a bug: `MaterialPreview` mounts its own canvas, and with
// two backends in the codebase it silently got the wrong one.
//
// `?renderer3d=webgl` is still ACCEPTED and resolves to the default: a stale URL
// or bookmark should render, and should say why it is not getting what it asked
// for.
//
// WHAT IS NOT PORTED, and is therefore visibly different — both LABELLED at
// runtime rather than silent, because the alternative in each case is a
// `ShaderMaterial` that throws inside `_renderObjectDirect` mid-frame and leaves
// every later render for every scene drawing nothing:
//
//   - HAIR STRANDS render as the exporter's CARDS mesh. `HairStrandMaterial`
//     splices ~200 lines of Kajiya-Kay, volume shadowing and per-strand skinning
//     into a `LineMaterial`; the port target is `Line2NodeMaterial` and it is a
//     piece of work in its own right (`hairStrands.js` says so at the gate).
//   - The PROBE REFERENCE SPHERE's SH-oracle ball is not drawn (a dev
//     diagnostic behind two toggles; the chrome ball beside it still is).
//
// The toon material model is GONE rather than unported — nothing in the
// catalogue bound one, so there was no feature to keep working. A stored row
// that still carries its output node is refused BY NAME by the compiler, which
// is contained the same way any other bad row is: `buildMaterialsFromAssets`
// catches per row, the mesh wears the neutral placeholder, and the binding
// report names it.

import { requestSharedGpuDevice } from './sharedGpuDevice.js'

/** @typedef {'webgpu'|'webgl-fallback'} CanvasRenderer */

const RENDERERS = new Set(['webgpu', 'webgl-fallback'])

/** Accepted and folded to the default: the classic renderer this named is gone,
 *  and a stale URL should render rather than fail. */
const RETIRED = new Set(['webgl'])

/**
 * @param {string|null} flag  the raw `?renderer3d=` value
 * @returns {{renderer: CanvasRenderer, reason: string, explicit: boolean}}
 */
export function resolveCanvasRenderer(flag) {
  const raw = flag == null ? null : String(flag).trim().toLowerCase()
  if (raw) {
    if (RENDERERS.has(raw)) return { renderer: raw, reason: `?renderer3d=${raw}`, explicit: true }
    if (RETIRED.has(raw)) {
      console.warn(`[canvas] ?renderer3d=${raw} named R3F's classic WebGLRenderer, which is gone — `
        + 'there is one renderer now and it compiles the node materials everything here uses. '
        + 'Using the default.')
    } else {
      console.warn(`[canvas] ignoring ?renderer3d=${flag} — expected one of ${[...RENDERERS].join(' | ')}`)
    }
  }
  return {
    renderer: 'webgpu',
    reason: "three is the only renderer; WebGPU where the browser has it, three's WebGL2 backend where it does not",
    explicit: false,
  }
}

export function canvasRendererFlag() {
  if (typeof window === 'undefined') return null
  try {
    return new URLSearchParams(window.location.search).get('renderer3d')
  } catch {
    return null
  }
}

/**
 * The `gl` prop for R3F's `<Canvas>`: a factory returning an initialised
 * `WebGPURenderer`.
 *
 * ASYNC ON PURPOSE. `WebGPURenderer` is not usable until `init()` resolves —
 * R3F 9 awaits a promise here, and returning the renderer before it is ready
 * produces a blank canvas rather than an error.
 *
 */
export function canvasGlFactory() {
  const { renderer } = resolveCanvasRenderer(canvasRendererFlag())
  const forceWebGL = renderer === 'webgl-fallback'
  return async (props) => {
    // ONE RENDERER PER CANVAS, and this is not an optimisation.
    //
    // The app runs under `React.StrictMode`, so R3F mounts the Canvas twice and
    // this factory is invoked more than once for the SAME canvas element. A
    // WebGPU canvas has exactly one context: every `new WebGPURenderer({canvas})`
    // calls `canvas.getContext('webgpu')` and reconfigures it, so the second one
    // silently takes ownership and the renderer R3F is actually driving draws
    // into a dead configuration.
    //
    // The symptom is the worst kind: a BLACK VIEWPORT AND NO ERRORS. Three
    // `[canvas] WebGPURenderer ready` lines in the log were the only clue, and
    // they read as harmless chatter. Memoising on the canvas makes the second
    // mount reuse the first renderer, which is what R3F expects anyway.
    const canvas = props?.canvas
    if (canvas && _byCanvas.has(canvas)) return _byCanvas.get(canvas)

    const pending = (async () => {
      const { WebGPURenderer } = await import('three/webgpu')
      // BEFORE THE FIRST MATERIAL COMPILES. three leaves the light probe's SH
      // array in the per-object uniform group, so it is uploaded once and the
      // baked probe never moves again — the avatar carries the lighting of
      // wherever it first stood. See lightProbeNodePatch.js for the measurement.
      const { applyLightProbeGroupPatch } = await import('./lightProbeNodePatch.js')
      await applyLightProbeGroupPatch()
      // MSAA IS OFF BY DEFAULT; `?aa=1` turns it back on.
      //
      // MSAA shades once per FRAGMENT and resolves coverage per SAMPLE, so it
      // costs most where fragments are partially covered — which is most of them
      // when a splat is small on screen. Measured on a 2.4M-splat environment it
      // is ~4x in a wide view and ~10% in a close-up product view, where splats
      // cover many pixels fully (numbers and hardware in docs/OGS_ARCHITECTURE.md).
      // Reproduced in a stock three project with none of our code, by flipping
      // this flag alone — which is what proved the gap was this and not our
      // splat, our sort, the SH or the tone curve.
      //
      // What it buys back is real but narrow: splats are soft-edged blended quads
      // whose silhouette the Gaussian falloff already antialiases, so MSAA does
      // almost nothing for them. It is the MESH geometry — GLB characters, hair
      // strands, the gizmos — whose edges this was for, and those now alias. That
      // is the accepted trade; `?aa=1` is the way back for a comparison render.
      const aa = (() => {
        try { return new URLSearchParams(location.search).get('aa') === '1' }
        catch { return false }
      })()
      // THE PAGE'S ONE DEVICE, if there is one. Handing it in is what lets the
      // splats — lifted into GPU buffers on ORT's device — be drawn by THIS
      // renderer, in this scene, instead of by a second renderer on a second
      // canvas composited over it. three only `destroy()`s a device it created
      // itself, so sharing costs nothing on teardown. Null (no WebGPU, or
      // `?gpudevice=0`) leaves three to do exactly what it did before, including
      // its own WebGL2 fallback.
      const device = forceWebGL ? null : await requestSharedGpuDevice()
      const r = new WebGPURenderer({
        ...props, antialias: aa, forceWebGL, ...(device ? { device } : {}),
      })
      await r.init()
      // The backend is read off the RENDERER, never from the flag: three falls
      // back on its own when WebGPU is missing, so a `?renderer3d=webgpu` run on
      // a machine without it is already the fallback and a log line quoting the
      // flag would say otherwise.
      const backend = r.backend?.isWebGPUBackend ? 'WebGPU' : 'WebGL fallback'
      console.info(`[canvas] three is the renderer: WebGPURenderer on ${backend}`
        + `${device ? ", on the page's one shared device" : ''}`
        + `${forceWebGL ? ' (forced by ?renderer3d=webgl-fallback)' : ''}`)
      // A debug seam, and on this path it is the only handle there is: R3F keeps
      // its renderer inside the Canvas root, so without this nothing outside can
      // ask what the renderer is doing — which is exactly what a black viewport
      // with no errors needs.
      if (typeof window !== 'undefined') window.__CANVAS_GL__ = r
      return r
    })()
    if (canvas) _byCanvas.set(canvas, pending)
    return pending
  }
}

/** canvas -> the renderer (or its in-flight promise) built for it. A WeakMap so a
 *  discarded canvas takes its renderer with it. */
const _byCanvas = new WeakMap()
